import { useEffect, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import type {
  CustomerFormValues,
  TravelBookingFormValues,
} from "../lib/schemas/travel-booking";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CirclePlus, Edit, Plus, Trash } from "lucide-react";
import { CustomerModal } from "./customer-modal";
import { ProductModal } from "./product-modal";
import { useFieldArray } from "react-hook-form";
import { FormField } from "../ui-form/form-field";
import { Input } from "@/components/ui/input";
import Select from "react-select";
import { useQuery, useQueryClient } from "react-query";
import { CustomerResponse, getCustomers } from "../apis";
import { useDebounce } from "use-debounce";
import {
  customerSingleConfig,
  step2,
  products as productsConfig,
} from "../lib/FormConfigs";
import { renderField } from "../lib/FormConfigs/Renderer";
import { FieldConfig } from "../lib/FormConfigs/config";
import CreatableSelect from "react-select/creatable";
import { AutoComplete } from "@/components/ui/autocomplete-search";
import { fi } from "date-fns/locale";

interface CustomerStepProps {
  form: UseFormReturn<TravelBookingFormValues>;
  config?: FieldConfig[];
}

export interface CustomerOption {
  value: string;
  label: string;
  customer: {
    phone_number: string;
    full_name: string;
    email: string;
    pan_no: string;
    id: string;
  };
}

export function CustomerStep({ form, config }: CustomerStepProps) {
  const { fields, append, update, remove } = useFieldArray({
    control: form.control,
    name: "customers",
  });
  const customers = form.getValues("customers") || [];
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [currentCustomerIndex, setCurrentCustomerIndex] = useState<
    number | null
  >(null);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [debouncedSearchTerm] = useDebounce(searchTerm, 800);
  const new_order_type =
    new URLSearchParams(window.location.search).get("type") || "";
  const customerStep = step2[new_order_type as keyof typeof step2];
  const productConfig =
    productsConfig[new_order_type as keyof typeof productsConfig];

  const { data: customerSuggestions, isLoading: isLoadingCustomers } = useQuery<
    CustomerResponse[]
  >({
    queryKey: ["customers", debouncedSearchTerm],
    queryFn: () => {
      if (!debouncedSearchTerm || debouncedSearchTerm.trim() === "") {
        return [];
      }
      return getCustomers(searchTerm);
    },
    enabled: searchTerm.length >= 4 && debouncedSearchTerm.trim() !== "",
    staleTime: 1000,
  });
  const handleCustomerSelection = (selected: CustomerOption | null) => {
    if (selected) {
      // Populate form with selected customer data
      form.setValue("customers.0.mobileNumber", selected.customer.phone_number);
      form.setValue("customers.0.fullName", selected.customer.full_name);
      form.setValue("customers.0.email", selected.customer.email);
      form.setValue("customers.0.pan", selected.customer.pan_no);
    }
  };

  const handleAddCustomer = (customer: CustomerFormValues) => {
    if (editMode && currentCustomerIndex !== null) {
      update(currentCustomerIndex, customer);
    } else {
      append(customer);
      const orderType = form.getValues(
        "customers.0.products.0.productType.type"
      );
      form.setValue("order_type", orderType as "buy" | "sell");
    }
    setIsCustomerModalOpen(false);
    setEditMode(false);
    setCurrentCustomerIndex(null);
  };

  const handleEditCustomer = (index: number) => {
    setCurrentCustomerIndex(index);
    setEditMode(true);
    setIsCustomerModalOpen(true);
  };

  const handleAddProduct = (index: number) => {
    setCurrentCustomerIndex(index);
    setIsProductModalOpen(true);
  };

  const handleSaveCustomer = () => {
    const customerData = form.getValues("customers.0");
    if (customerData?.mobileNumber || customerData?.fullName) {
      append(customerData);
      form.setValue("customers.0", {
        mobileNumber: "",
        fullName: "",
        email: "",
        pan: "",
        products: [],
      });
    }
  };
  const products = form.watch("customers.0.products") || [];

  useEffect(() => {
    const customersInForm = form.getValues("customers");

    if (
      fields.length === 0 &&
      Array.isArray(customersInForm) &&
      customersInForm.length > 0
    ) {
      customersInForm
        .filter((customer) => customer && Object.keys(customer).length > 0)
        .forEach((customer: CustomerFormValues) => {
          append(customer);
        });
    }
  }, []);

  return (
    <div className="space-y-6">
      {products?.length ? (
        <div className="space-y-4">
          <h3 className="text-lg flex items-center font-semibold">
            Customers
            <Button
              onClick={() => {
                setIsCustomerModalOpen(true);
                setEditMode(false);
                setCurrentCustomerIndex(null);
              }}
              className="text-blue-700"
            >
              <svg
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 15.7109C4.14 15.7109 1 12.5709 1 8.71094C1 4.85094 4.14 1.71094 8 1.71094C11.86 1.71094 15 4.85094 15 8.71094C15 12.5709 11.86 15.7109 8 15.7109ZM8 2.71094C4.69 2.71094 2 5.40094 2 8.71094C2 12.0209 4.69 14.7109 8 14.7109C11.31 14.7109 14 12.0209 14 8.71094C14 5.40094 11.31 2.71094 8 2.71094Z"
                  fill="#165FE3"
                />
                <path
                  d="M8 12.2109C7.72 12.2109 7.5 11.9909 7.5 11.7109V5.71094C7.5 5.43094 7.72 5.21094 8 5.21094C8.28 5.21094 8.5 5.43094 8.5 5.71094V11.7109C8.5 11.9909 8.28 12.2109 8 12.2109Z"
                  fill="#165FE3"
                />
                <path
                  d="M11 9.21094H5C4.72 9.21094 4.5 8.99094 4.5 8.71094C4.5 8.43094 4.72 8.21094 5 8.21094H11C11.28 8.21094 11.5 8.43094 11.5 8.71094C11.5 8.99094 11.28 9.21094 11 9.21094Z"
                  fill="#165FE3"
                />
              </svg>
              Add
            </Button>
          </h3>
          <div className="w-full overflow-x-auto">
            <Table className="rounded-t-xl overflow-hidden shadow-none border-0 bg-transparent min-w-max">
              <TableHeader className="bg-[#F5F6FA] rounded-t-xl">
                <TableRow className="text-[#222] font-semibold text-base leading-5">
                  <TableHead className="px-6 py-3 text-left">
                    Customer Name
                  </TableHead>
                  <TableHead className="px-6 py-3 text-left">
                    Products
                  </TableHead>
                  {/* <TableHead className="px-6 py-3 text-left">Product type</TableHead> */}
                  <TableHead className="px-6 py-3 text-left">
                    FCY Amount
                  </TableHead>
                  <TableHead className="px-6 py-3 text-left">
                    INR Amount
                  </TableHead>
                  <TableHead className="px-6 py-3 text-left">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="bg-white">
                {(() => {
                  // Extract only valid customers with full data
                  const validCustomers = fields
                    ?.map((field, index) => {
                      const customer = form.getValues(
                        `customers.${index}`
                      ) as CustomerFormValues;
                      return {
                        index, // original index in `fields`
                        customer,
                        field,
                      };
                    })
                    ?.filter(
                      (item) =>
                        item.customer?.fullName &&
                        item.customer?.products?.length > 0 &&
                        item.customer?.customerId
                    );

                  // Remove duplicates by customerId
                  const uniqueCustomersMap = new Map();
                  validCustomers?.forEach((item) => {
                    if (!uniqueCustomersMap.has(item.customer.customerId)) {
                      uniqueCustomersMap.set(item.customer.customerId, item);
                    }
                  });

                  const uniqueCustomers = Array.from(
                    uniqueCustomersMap.values()
                  );

                  if (uniqueCustomers.length === 0) {
                    return (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center text-gray-500 py-6"
                        >
                          No customers added yet.
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return uniqueCustomers.map(
                    ({ customer, field, index: originalIndex }) => {
                      let totalAmount = 0;
                      let totalValue = 0;

                      customer.products.forEach(
                        (product: { currencies: any[] }) => {
                          product?.currencies?.forEach((currency) => {
                            const amount = currency?.amount ?? 0;
                            const rate = currency?.rate ?? 0;
                            totalAmount += amount;
                            totalValue += amount * rate;
                          });
                        }
                      );

                      return (
                        <TableRow
                          key={field.id}
                          className={`px-6 ${
                            originalIndex === fields.length - 1
                              ? "rounded-b-xl"
                              : ""
                          } border-0`}
                        >
                          <TableCell className="px-6 py-4 text-[#222] font-medium text-base">
                            {customer?.fullName || "—"}
                          </TableCell>
                          <TableCell className="px-6 py-4 text-[#222] font-normal text-base">
                            {customer?.products
                              ?.map((p:any) => p?.productType?.label)
                              .filter(Boolean)
                              .join(", ") || "—"}
                          </TableCell>
                          <TableCell className="px-6 py-4 text-[#222] font-normal text-base">
                            {totalAmount}
                          </TableCell>
                          <TableCell className="px-6 py-4 text-[#222] font-normal text-base">
                            {totalValue.toFixed(2)}
                          </TableCell>
                          <TableCell className="px-6 py-4">
                            <div className="flex flex-col items-start gap-2">
                              <button
                                className="text-[#ff4d4f] hover:underline flex items-center"
                                onClick={() => remove(originalIndex)}
                                title="Delete"
                                type="button"
                              >
                                <Trash className="h-4 w-4 mr-1" />
                              </button>
                              <button
                                className="text-[#176bef] hover:underline flex items-center"
                                onClick={() => handleAddProduct(originalIndex)}
                                title="Add product"
                                type="button"
                              >
                                <CirclePlus className="h-4 w-4 mr-1" />
                                <span>Add product</span>
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    });
                })()}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : (
        <div className="space-y-4 max-w-sm">
          <div className="grid grid-cols-[auto_1fr] gap-x-[18.5px]">
            {config?.map((item, index) => {
              const isCompleted = form.watch(item.name as any);
              const isLast = index === config.length - 1;
              return (
                <>
                  <div
                    key={`indicator-${item.name}`}
                    className="flex flex-col items-center justify-start"
                  >
                    {/* Circle indicator */}
                    <div
                      className={`
                      w-[9px] h-[9px] rounded-full border mt-1.5 flex-shrink-0
                      transition-colors duration-300
                      ${
                        isCompleted
                          ? "bg-blue-700 border-blue-700"
                          : "bg-white-A700 border-blue-700"
                      }
                    `}
                    />

                    {/* Dotted line (except for last item) */}
                    {!isLast && (
                      <div
                        className={`w-0.5 flex-1 border-l-2 border-dotted min-h-[1.5rem] ${
                          isCompleted ? "border-blue-700 " : "border-gray-300 "
                        }`}
                      />
                    )}
                  </div>
                  {item.name === "customers.0.mobileNumber" ? (
                    <div key={`field-${item.name}`} className="pb-6">
                      <FormField
                        key={index}
                        isOptionalField
                        name="customers.0.mobileNumber"
                        label="What is the mobile number of customer?"
                        render={({ field }) => (
                          // <Select<CustomerOption>
                          //   options={
                          //     customerSuggestions?.map((customer) => ({
                          //       value: customer?.phone_number,
                          //       label: `${customer?.full_name} (${customer?.phone_number})`,
                          //       customer: {
                          //         phone_number: customer?.phone_number,
                          //         full_name: customer?.full_name,
                          //         email: customer?.email,
                          //         pan_no: customer?.pan_no,
                          //         id: customer?.id.toString(),
                          //       },
                          //     })) || []
                          //   }
                          //   onChange={(selected) => {
                          //     field.onChange(selected?.value || "");
                          //     if (selected && (selected as any)?.customer) {
                          //       form.setValue(
                          //         "customers.0.customerId",
                          //         (selected as CustomerOption)?.customer.id ||
                          //           ""
                          //       );
                          //       handleCustomerSelection(
                          //         selected as CustomerOption
                          //       );
                          //     } else {
                          //       form.setValue("customers.0.customerId", "");
                          //     }
                          //   }}
                          //   components={{ IndicatorSeparator: () => null }}
                          //   onInputChange={(inputValue) =>
                          //     setSearchTerm(inputValue)
                          //   }
                          //   isLoading={isLoadingCustomers}
                          //   placeholder="Search and select customer"
                          //   className="react-select-container"
                          //   classNamePrefix="react-select"
                          //   styles={{
                          //     control: (base, state) => ({
                          //       ...base,
                          //       padding: "8px",
                          //       borderRadius: "8px",
                          //       borderColor: state.isFocused
                          //         ? "#3b82f6"
                          //         : base.borderColor,
                          //       boxShadow: state.isFocused
                          //         ? "0 0 0 1px #3b82f6"
                          //         : base.boxShadow,
                          //       "&:hover": {
                          //         borderColor: "#3b82f6",
                          //       },
                          //     }),
                          //     valueContainer: (base) => ({
                          //       ...base,
                          //       padding: "0 8px",
                          //     }),
                          //     singleValue: (base) => ({
                          //       ...base,
                          //       fontSize: "14px",
                          //       fontWeight: 500,
                          //       color: "#165FE3",
                          //     }),
                          //   }}
                          // />
                          <AutoComplete    
                            value={{
                              value: form.getValues("customers.0.mobileNumber"),
                              label: form.getValues("customers.0.mobileNumber"),
                            }}                        
                            onValueChange={sel => {
                              const customer = customerSuggestions?.find(
                                (c) => c.id?.toString() === sel?.value
                              );
                              if (customer?.id) {
                                field.onChange(customer?.phone_number || "");
                                form.setValue(
                                  "customers.0.customerId",
                                  customer.id.toString()
                                );
                                handleCustomerSelection({
                                  value: customer.phone_number,
                                  label: `${customer.full_name} (${customer.phone_number})`,
                                  customer: {
                                    phone_number: customer.phone_number,
                                    full_name: customer.full_name,
                                    email: customer.email,
                                    pan_no: customer.pan_no,
                                    id: customer.id.toString(),
                                  },
                                });
                              } else {
                                form.setValue("customers.0.customerId", "");
                                field.onChange(sel.label || "");
                              }
                              setSearchTerm(field.value || "");
                            }}
                            options={
                              customerSuggestions?.map((customer) => ({
                                value: customer?.id.toString(),
                                label: `${customer?.full_name} (${customer?.phone_number})`,
                              })) || []
                            }

                            inputValue={searchTerm}
                            onInputChange={(value) => {
                              if(value.length < 10 && form.getValues("customers.0.customerId")) {
                                form.setValue("customers.0.customerId", "");
                                handleCustomerSelection({
                                  value: form.getValues("customers.0.mobileNumber"),
                                  label: form.getValues("customers.0.mobileNumber"),
                                  customer: {
                                    phone_number: '',
                                    full_name: '',
                                    email: '',
                                    pan_no: '',
                                    id: '',
                                  },
                                });
                              }
                              setSearchTerm(value);
                              field.onChange(value);
                            }}
                            emptyMessage="No customers found"
                            isLoading={isLoadingCustomers}
                            placeholder="Search and select customer"
                          />
                        )}
                      />
                    </div>
                  ) : (
                    <div key={`field-${item.name}`} className="pb-6">
                      <FormField
                        key={index}
                        isOptionalField={item.required}
                        name={item.name}
                        label={item.label}
                        render={({ field }) => renderField(item, field)}
                      />
                    </div>
                  )}
                </>
              );
            })}
          </div>
        </div>
      )}

      {customers.length > 0 && (
        <CustomerModal
          productConfig={productConfig}
          config={customerStep}
          isOpen={isCustomerModalOpen}
          onClose={() => setIsCustomerModalOpen(false)}
          onSubmit={handleAddCustomer}
          initialData={
            editMode && currentCustomerIndex !== null
              ? form.getValues(`customers.${currentCustomerIndex}`)
              : undefined
          }
        />
      )}

      {isProductModalOpen && (
        <ProductModal
          config={productConfig}
          isOpen={isProductModalOpen}
          order_type={form.getValues("order_type")}
          onClose={() => setIsProductModalOpen(false)}
          onSubmit={(product) => {
            if (currentCustomerIndex !== null) {
              const customer = form.getValues(
                `customers.${currentCustomerIndex}`
              ) as CustomerFormValues;
              const updatedCustomer = {
                ...customer,
                products: [...(customer?.products || []), product],
              };
              update(currentCustomerIndex, updatedCustomer);
            }
            setIsProductModalOpen(false);
          }}
        />
      )}
    </div>
  );
}
